"use client";

import { ArrowRight, Brain, Lightbulb, Target } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function AcademicServices() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    once: true,
    threshold: 0.2
  });
  const services = [{
    id: 1,
    icon: Brain,
    title: "AI-Driven Assessments",
    description: "Personalized evaluation tools powered by artificial intelligence to identify learning needs and track student progress in real time.",
    link: "/ServicesPage"
  }, {
    id: 2,
    icon: Lightbulb,
    title: "Behavioral Support",
    description: "Expert guidance for students with special behavioral needs, utilizing proven strategies and compassionate approaches to foster growth.",
    link: "/ServicesPage"
  }, {
    id: 3,
    icon: Target,
    title: "Technology-Enhanced Learning",
    description: "Mobile and virtual instruction in ELA, science, and social studies, tailored to diverse learning styles and accessibility requirements.",
    link: "/ServicesPage"
  }];
  return <section ref={ref} className="relative overflow-hidden bg-light-background py-16 px-6 sm:px-8 lg:px-12">
      {}
      <div className="absolute inset-0 pointer-events-none opacity-5" style={{
      backgroundImage: `repeating-linear-gradient(
            45deg,
            hsl(190, 80%, 45%),
            hsl(190, 80%, 45%) 2px,
            transparent 2px,
            transparent 12px
          )`,
      animation: "drift 40s linear infinite"
    }} />
      <style jsx>{`
        @keyframes drift {
          0% {
            transform: translate(0, 0);
          }
          100% {
            transform: translate(24px, 24px);
          }
        }
      `}</style>

      <div className="relative z-10 mx-auto max-w-7xl">
        <div className="mb-12 text-center">
          <h2 className="text-3xl sm:text-4xl font-display font-bold text-foreground-light mb-4">
            Academic Services
          </h2>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            Delivering inclusive, technology-enhanced education for K-12 students with diverse learning needs.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {services.map((service, i) => {
          const Icon = service.icon;
          const animationClass = isVisible ? i === 0 ? "animate-fade-in-up" : i === 1 ? "animate-fade-in-up-delay-100" : "animate-fade-in-up-delay-200" : "opacity-0";
          return <Card key={service.id} data-index={i} className={`
                  relative flex flex-col h-full
                  border border-border rounded-lg shadow-soft
                  hover:shadow-medium hover:-translate-y-1 hover:scale-[1.02]
                  transition-all duration-[var(--duration-medium)] ease-[var(--ease-out-back)]
                  ${animationClass}
                `}>
                <CardHeader className="pb-4">
                  <div className="mb-4 inline-flex items-center justify-center w-16 h-16 rounded-full bg-primary-light">
                    <Icon className="w-8 h-8 text-primary" />
                  </div>
                  <CardTitle className="text-xl font-display font-semibold text-foreground-light">
                    {service.title}
                  </CardTitle>
                </CardHeader>
                <CardContent className="flex-1 flex flex-col justify-between">
                  <p className="text-muted-foreground mb-6">{service.description}</p>
                  <div className="flex justify-end">
                    <Link to={service.link}>
                      <Button variant="ghost" size="sm" className="group hover:bg-primary-light hover:text-primary hover:scale-105 hover:shadow-soft transition-all duration-[var(--duration-fast)] ease-[var(--ease-spring)]">
                        Learn More
                        <ArrowRight className="w-4 h-4 ml-1 group-hover:translate-x-0.5 transition-transform duration-[var(--duration-fast)]" />
                      </Button>
                    </Link>
                  </div>
                </CardContent>
              </Card>;
        })}
        </div>
      </div>
    </section>;
}